// src/pages/license/LicenseSettingsPage.tsx
// Signing keys — the Ed25519 key pair this platform (as master) uses to
// sign issued licenses. Local instances use the public key to verify a
// license offline, without contacting master.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { observer } from '@react';
import { useLicense, useI18n } from '@react/hooks';
import { Button, Alert } from '@react/components';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import type { GetPublicKeyResponse } from '@core/types/license';
import { errMessage, fmtDateTime } from '@/features/license/format';

export const LicenseSettingsPage = observer(() => {
  const { t, locale } = useI18n();
  const store = useLicense();

  const [key, setKey] = useState<GetPublicKeyResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [rotateOpen, setRotateOpen] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = () => {
    setIsLoading(true);
    setError(null);
    store
      .GetPublicKey()
      .then(setKey)
      .catch((e: unknown) => setError(errMessage(e, t('license.settings.loadError'))))
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const copyKey = async () => {
    if (!key) return;
    try {
      await navigator.clipboard.writeText(key.public_key);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard unavailable — key is still visible
    }
  };

  const rotate = async () => {
    setRotating(true);
    setError(null);
    try {
      await store.RotateSigningKey({ entity: undefined, algorithm: 'Ed25519' });
      setRotateOpen(false);
      load();
    } catch (e) {
      setError(errMessage(e, t('license.settings.rotateError')));
      setRotateOpen(false);
    } finally {
      setRotating(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-4 p-6">
      <div className="flex items-center justify-between">
        {/* The tab already names this page; the heading stays for screen readers. */}
        <h1 className="sr-only">{t('license.settings.title')}</h1>
        <Link to="/license" className="ml-auto text-sm text-primary hover:underline">{t('license.backToLicenses')}</Link>
      </div>

      {error && <Alert variant="error">{error}</Alert>}

      <div className="rounded-lg border border-border p-4">
        <h2 className="text-sm font-semibold text-foreground">{t('license.settings.signingKey')}</h2>
        <p className="mt-1 text-sm text-muted-foreground">{t('license.settings.signingKeyHint')}</p>

        {isLoading && <div className="mt-3 h-20 animate-pulse rounded-md bg-muted" aria-busy="true" />}

        {!isLoading && key && (
          <div className="mt-3 space-y-2">
            <div className="rounded-md border border-border bg-muted p-3">
              <pre className="whitespace-pre-wrap break-all font-mono text-xs text-foreground">{key.public_key}</pre>
            </div>
            <dl className="grid grid-cols-2 gap-2 text-sm">
              <div><dt className="text-xs text-muted-foreground">{t('license.settings.keyId')}</dt><dd className="text-foreground">{key.key_id}</dd></div>
              <div><dt className="text-xs text-muted-foreground">{t('license.settings.algorithm')}</dt><dd className="text-foreground">{key.algorithm}</dd></div>
              <div><dt className="text-xs text-muted-foreground">{t('license.settings.createdAt')}</dt><dd className="text-foreground">{fmtDateTime(key.created_at, locale)}</dd></div>
              {key.expires_at && <div><dt className="text-xs text-muted-foreground">{t('license.settings.expiresAt')}</dt><dd className="text-foreground">{fmtDateTime(key.expires_at, locale)}</dd></div>}
            </dl>
            <div className="flex gap-2">
              <Button size="sm" variant="secondary" onClick={copyKey}>{copied ? t('license.settings.copied') : t('license.settings.copyKey')}</Button>
              <Button size="sm" variant="danger" onClick={() => setRotateOpen(true)}>{t('license.settings.rotate')}</Button>
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={rotateOpen}
        title={t('license.settings.rotate')}
        description={t('license.settings.rotateConfirm')}
        destructive
        isConfirming={rotating}
        onCancel={() => setRotateOpen(false)}
        onConfirm={rotate}
      />
    </div>
  );
});
